// Role Form Component - Separate page for adding/editing roles
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  Grid,
  Paper,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import apiService from '../../services/api';

const RoleForm = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get role ID from URL if editing
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active',
    permissions: {}
  });

  const isEditing = !!id;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchModules();
    if (isEditing) {
      fetchRoleDetails();
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchModules = async () => {
    try {
      const response = await apiService.get('/roles/modules');
      setModules(response.data || []);
    } catch (error) {
      console.error('Error fetching modules:', error);
      setError('Failed to fetch modules');
    }
  };

  const fetchRoleDetails = async () => {
    try {
      const response = await apiService.get(`/roles/${id}`);
      const roleData = response.data;
      
      setFormData({
        name: roleData.name || '',
        description: roleData.description || '',
        status: roleData.status || 'active',
        permissions: roleData.parsed_permissions || {}
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching role details:', error);
      setError('Failed to fetch role details');
      setLoading(false);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePermissionChange = (moduleId, permissionId, checked) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permissionId]: checked
      }
    }));
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      const submitData = {
        name: formData.name,
        description: formData.description,
        status: formData.status,
        permissions: formData.permissions
      };

      if (isEditing) {
        await apiService.put(`/roles/${id}`, submitData);
      } else {
        await apiService.post('/roles', submitData);
      }

      navigate('/dashboard/roles');
    } catch (error) {
      console.error('Error saving role:', error);
      setError(error.response?.data?.error || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2,   margin: '0 auto' }}>
      {/* Compact Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button
          onClick={() => navigate('/dashboard/roles')}
          startIcon={<ArrowBackIcon />}
          sx={{ minWidth: 'auto', p: 1 }}
        >
          Back
        </Button>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
          {isEditing ? 'Edit Role' : 'Create New Role'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Single combined card: info + permissions + submit */}
      <Paper sx={{ p: 2, mb: 2 }}>
        {/* Basic information */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Role Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Status"
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              SelectProps={{ native: true }}
              size="small"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </TextField>
          </Grid>
        </Grid>
 

        {/* Permissions */}
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
          Permissions:
        </Typography>

        <Grid container spacing={2}>
          {/* Left column card */}
          <Grid item xs={12} sm={6}>
            <Paper sx={{ p: 2, height: '100%' }} variant="outlined">
              {modules.filter((_, i) => i % 2 === 0).map((module, moduleIndex, list) => (
                <Box key={module.id} sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {module.display_name || module.name}
                  </Typography>

                  <Grid container spacing={1.5}>
                    {(module.permissions && Array.isArray(module.permissions) ? module.permissions : []).map((permission) => (
                      <Grid item xs={12} sm={6} md={3} key={permission.id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.permissions[permission.id] || false}
                              onChange={(e) => handlePermissionChange(
                                module.id,
                                permission.id,
                                e.target.checked
                              )}
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                              {permission.display_name || permission.action}
                            </Typography>
                          }
                          sx={{ m: 0 }}
                        />
                      </Grid>
                    ))}
                  </Grid>

                  {moduleIndex < list.length - 1 && (
                    <Divider sx={{ mt: 2 }} />
                  )}
                </Box>
              ))}
            </Paper>
          </Grid>

          {/* Right column card */}
          <Grid item xs={12} sm={6}>
            <Paper sx={{ p: 2, height: '100%' }} variant="outlined">
              {modules.filter((_, i) => i % 2 === 1).map((module, moduleIndex, list) => (
                <Box key={module.id} sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {module.display_name || module.name}
                  </Typography>

                  <Grid container spacing={1.5}>
                    {(module.permissions && Array.isArray(module.permissions) ? module.permissions : []).map((permission) => (
                      <Grid item xs={12} sm={6} md={3} key={permission.id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.permissions[permission.id] || false}
                              onChange={(e) => handlePermissionChange(
                                module.id,
                                permission.id,
                                e.target.checked
                              )}
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                              {permission.display_name || permission.action}
                            </Typography>
                          }
                          sx={{ m: 0 }}
                        />
                      </Grid>
                    ))}
                  </Grid>

                  {moduleIndex < list.length - 1 && (
                    <Divider sx={{ mt: 2 }} />
                  )}
                </Box>
              ))}
            </Paper>
          </Grid>
        </Grid>

        {/* Submit button after permissions */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            onClick={handleSubmit}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!formData.name.trim() || saving}
            sx={{ borderRadius: 1, textTransform: 'none' }}
          >
            {saving ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default RoleForm;